/**
 * testIdentity — the test-identity rules (Bill 2026-09-15), mirrored from the
 * server's TestIdentityRules so the officer sees the problem while typing.
 *
 *   "strict adherence to 'Test' used as the last name and '666' prefix for any
 *    typed CIF/SSN/TIN/ATIN and allow the '000' to generate automatically for
 *    any 'do not have a tax id number' CIFs"
 *
 * - The last name "Test" and a 666 SSN/TIN/ATIN/CIF travel together.
 * - A test customer with no tax ID is fine: Core Director generates a 000 CIF.
 * - A test application holds only test customers (beneficiaries included).
 *
 * The server enforces the same rules at CustAdd and at commit validation; this
 * copy only makes them visible early. Prefixes and the reserved last name are
 * SystemSettings (seed 193): test.cifPrefix, test.lastName, test.generatedCifPrefix.
 */
import type { GateResult } from '@/services/workflow-engine/types';

export interface TestIdentitySettings {
  cifPrefix: string;
  lastName: string;
  generatedCifPrefix: string;
}

export const DEFAULT_TEST_IDENTITY: TestIdentitySettings = {
  cifPrefix: '666',
  lastName: 'Test',
  generatedCifPrefix: '000',
};

/** Tolerates the /v3/config/system-settings row array and a flat record. */
export function readTestIdentitySettings(systemSettings: unknown): TestIdentitySettings {
  const read = (key: string): string | undefined => {
    if (Array.isArray(systemSettings)) {
      for (const row of systemSettings as ReadonlyArray<Record<string, unknown>>) {
        if (!row || typeof row !== 'object') continue;
        const k = typeof row.settingKey === 'string' ? row.settingKey : typeof row.key === 'string' ? row.key : undefined;
        if (k === key) {
          const v = row.settingValue ?? row.value;
          return typeof v === 'string' && v.trim() ? v.trim() : undefined;
        }
      }
      return undefined;
    }
    if (systemSettings && typeof systemSettings === 'object') {
      const v = (systemSettings as Record<string, unknown>)[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (v && typeof v === 'object') {
        const inner = (v as Record<string, unknown>).settingValue ?? (v as Record<string, unknown>).value;
        if (typeof inner === 'string' && inner.trim()) return inner.trim();
      }
    }
    return undefined;
  };
  return {
    cifPrefix: read('test.cifPrefix') ?? DEFAULT_TEST_IDENTITY.cifPrefix,
    lastName: read('test.lastName') ?? DEFAULT_TEST_IDENTITY.lastName,
    generatedCifPrefix: read('test.generatedCifPrefix') ?? DEFAULT_TEST_IDENTITY.generatedCifPrefix,
  };
}

export interface IdentityPartyLike {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  taxId?: string;
  tinCode?: string;
  cifId?: string;
  customerType?: string;
  name?: string;
}

export type IdentityKind = 'real' | 'test' | 'neutral';

const digits = (s?: string): string => (s ?? '').replace(/\D/g, '');

export function isEntityCustomerType(customerType?: string): boolean {
  const t = (customerType ?? '').trim().toLowerCase();
  return t.length > 0 && t !== 'individual' && t !== 'minor';
}

export function identityDisplayName(p: IdentityPartyLike): string {
  const composed = [p.firstName, p.middleName, p.lastName, p.suffix]
    .map((x) => (x ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return composed || (p.name ?? '').trim() || 'An applicant';
}

export function evaluateTestIdentity(
  p: IdentityPartyLike,
  s: TestIdentitySettings,
  entity: boolean = isEntityCustomerType(p.customerType),
): { kind: IdentityKind; violation?: string } {
  const who = identityDisplayName(p);
  let tax = digits(p.taxId);
  if (tax.length < 9) tax = ''; // only a complete number counts
  const cif = digits(p.cifId);
  const taxTest = tax.length > 0 && tax.startsWith(s.cifPrefix);
  const cifTest = cif.length > 0 && cif.startsWith(s.cifPrefix);
  const cifGenerated = cif.length > 0 && cif.startsWith(s.generatedCifPrefix);

  if (entity) {
    if (taxTest || cifTest) return { kind: 'test' };
    if (cifGenerated && tax.length === 0) return { kind: 'neutral' };
    return { kind: tax.length > 0 || cif.length > 0 ? 'real' : 'neutral' };
  }

  const lastTest = (p.lastName ?? '').trim().toLowerCase() === s.lastName.toLowerCase();
  if ((taxTest || cifTest) && !lastTest) {
    return {
      kind: 'test',
      violation: `${who}: a test SSN/TIN/ATIN or CIF starting with ${s.cifPrefix} needs the last name "${s.lastName}".`,
    };
  }
  if (lastTest) {
    if (tax.length > 0 && !taxTest) {
      return {
        kind: 'test',
        violation: `${who}: the last name "${s.lastName}" is only for test customers, and a test SSN/TIN/ATIN must start with ${s.cifPrefix}.`,
      };
    }
    if (tax.length === 0 && cif.length > 0 && !cifTest && !cifGenerated) {
      return {
        kind: 'test',
        violation: `${who}: a test customer's CIF must start with ${s.cifPrefix}, or ${s.generatedCifPrefix} when Core Director generated it for a customer with no tax ID number.`,
      };
    }
    return { kind: 'test' };
  }
  return { kind: tax.length > 0 || cif.length > 0 ? 'real' : 'neutral' };
}

export function testIdentityViolation(p: IdentityPartyLike, s: TestIdentitySettings): string | undefined {
  return evaluateTestIdentity(p, s).violation;
}

/**
 * TEST MODE (Pilot/Test, Bill 2026-10-07) save-time rule — mirror of the server's
 * TestIdentityRules.TestModeDraftViolation. A draft may be incomplete, but anything
 * typed must already be test: a real SSN/TIN/ATIN or CIF is refused, and an
 * individual's last name must be the test last name. The server enforces this on
 * every save; this only shows the reason while the officer types.
 */
export function testModeDraftViolation(p: IdentityPartyLike, s: TestIdentitySettings): string | undefined {
  const entity = isEntityCustomerType(p.customerType);
  const v = evaluateTestIdentity(p, s, entity);
  if (v.violation) return v.violation;
  const who = identityDisplayName(p);
  if (v.kind === 'real') {
    return `${who} is not a test customer. In Test mode a person needs the last name "${s.lastName}" and an SSN/TIN/ATIN or CIF starting with ${s.cifPrefix} (or no tax ID number).`;
  }
  const last = (p.lastName ?? '').trim();
  if (!entity && last.length > 0 && last.toLowerCase() !== s.lastName.toLowerCase()) {
    return `${who}: in Test mode every person's last name must be "${s.lastName}".`;
  }
  return undefined;
}

export function evaluateTestApplication(
  parties: ReadonlyArray<IdentityPartyLike & { entity?: boolean }>,
  s: TestIdentitySettings,
): { isTest: boolean; violations: string[] } {
  const violations: string[] = [];
  const real: string[] = [];
  let isTest = false;
  for (const p of parties) {
    const v = evaluateTestIdentity(p, s, p.entity ?? isEntityCustomerType(p.customerType));
    if (v.violation) violations.push(v.violation);
    if (v.kind === 'test') isTest = true;
    else if (v.kind === 'real') real.push(identityDisplayName(p));
  }
  if (isTest && real.length > 0) {
    const names = Array.from(new Set(real));
    violations.push(
      `A test application can only include test customers. ${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} not a test customer `
      + `(last name "${s.lastName}" with an SSN/TIN/ATIN or CIF starting with ${s.cifPrefix}). Remove them, or open a separate application for them.`,
    );
  }
  return { isTest, violations };
}

/** POD beneficiaries from cart metadata (beneficiaries: { [lineId]: BenefDraft[] }). */
export function beneficiaryParties(meta: Record<string, unknown> | undefined): Array<IdentityPartyLike & { entity: boolean }> {
  const out: Array<IdentityPartyLike & { entity: boolean }> = [];
  const map = meta?.['beneficiaries'];
  if (!map || typeof map !== 'object' || Array.isArray(map)) return out;
  for (const rows of Object.values(map as Record<string, unknown>)) {
    if (!Array.isArray(rows)) continue;
    for (const r of rows as ReadonlyArray<Record<string, unknown>>) {
      if (!r || typeof r !== 'object') continue;
      const str = (k: string): string | undefined => (typeof r[k] === 'string' ? (r[k] as string) : undefined);
      const noSsn = r['noSsn'] === true;
      out.push({
        firstName: str('firstName'),
        middleName: str('middleName'),
        lastName: str('lastName'),
        name: str('name'),
        taxId: noSsn ? '' : str('taxId') ?? str('ssn'),
        tinCode: noSsn ? 'N' : 'S',
        cifId: str('cifId'),
        entity: false,
      });
    }
  }
  return out;
}

/** Review-screen gate: blocks commit while any party breaks the test-identity rules. */
export function buildTestIdentityGate(
  parties: ReadonlyArray<IdentityPartyLike & { entity?: boolean }>,
  s: TestIdentitySettings,
  entityType: string | undefined,
): GateResult | null {
  const { violations } = evaluateTestApplication(parties, s);
  if (violations.length === 0) return null;
  return {
    gateCode: 'TEST_IDENTITY',
    entityType: entityType ?? '',
    category: 'structure',
    status: 'blocked',
    label: 'Test customers follow the test-identity rules',
    reason: violations.join(' '),
    remediationAction: { label: 'Fix people', deepLinkStepId: 'people' },
  };
}
